// ═══════════════════════════════════════════════════════════════
// sanitizeHtml.ts – függőség nélküli HTML-szanitáló (S6 javítás)
//
// A DOCX-előnézet (mammoth) kimenete eddig szűrés nélkül került a
// dangerouslySetInnerHTML-be. A mammoth a hivatkozások href-jét nem szűri,
// így egy kártékony DOCX `javascript:` linkkel vagy beágyazott HTML-lel
// kódot futtathatott az alkalmazás originjén (a localStorage-ban lévő
// munkamenet-token elérhető lett volna).
//
// Engedélyezőlistás megközelítés: csak ismert, szöveges/táblázatos
// elemek és ártalmatlan attribútumok maradnak; minden eseménykezelő,
// script, iframe, object, form, style stb. elem/attribútum törlődik;
// URL-ek csak http(s), mailto, tel, # és (képeknél) data:image sémával.
// ═══════════════════════════════════════════════════════════════

const ALLOWED_TAGS = new Set([
  'a', 'abbr', 'b', 'blockquote', 'br', 'caption', 'code', 'col', 'colgroup', 'dd', 'del', 'div', 'dl', 'dt',
  'em', 'figcaption', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'ins', 'kbd', 'li', 'mark',
  'ol', 'p', 'pre', 'q', 's', 'small', 'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th',
  'thead', 'tr', 'u', 'ul',
]);

// Ezek az elemek a tartalmukkal együtt törlődnek (nem csak „kicsomagolódnak”).
const DROP_WITH_CONTENT = new Set(['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template', 'svg', 'math', 'form', 'input', 'button', 'textarea', 'select', 'link', 'meta', 'base', 'frame', 'frameset']);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  '*': new Set(['title', 'colspan', 'rowspan', 'align', 'dir', 'lang', 'id']),
  a: new Set(['href', 'name']),
  img: new Set(['src', 'alt', 'width', 'height']),
  td: new Set(['valign']),
  th: new Set(['valign', 'scope']),
  ol: new Set(['start', 'type']),
};

// Formázott leíráshoz: csak szín, háttérszín és igazítás – minden más stílus törlődik
const STYLE_TAGS = new Set(['span', 'mark', 'p', 'td', 'th', 'h1', 'h2', 'h3', 'h4', 'li', 'div', 'col']);
const COLOR_VALUE = /^(#[0-9a-f]{3,8}|rgba?\(\s*[\d.]+%?\s*,\s*[\d.]+%?\s*,\s*[\d.]+%?\s*(,\s*[\d.]+\s*)?\)|[a-z]{3,20})$/i;
const ALIGN_VALUE = /^(left|right|center|justify)$/i;
const SIZE_VALUE = /^\d{1,4}(\.\d+)?(px|%|em)$/i;

/** a style attribútumból csak az engedélyezett, biztonságos értékű tulajdonságok maradnak */
export function cleanStyle(style: string): string {
  const out: string[] = [];
  for (const decl of String(style || '').split(';')) {
    const i = decl.indexOf(':');
    if (i < 0) continue;
    const prop = decl.slice(0, i).trim().toLowerCase();
    const val = decl.slice(i + 1).trim().replace(/\s*!important$/i, '');
    if ((prop === 'color' || prop === 'background-color') && COLOR_VALUE.test(val) && !/expression|url/i.test(val)) out.push(`${prop}: ${val}`);
    else if (prop === 'text-align' && ALIGN_VALUE.test(val)) out.push(`${prop}: ${val}`);
    else if ((prop === 'min-width' || prop === 'width') && SIZE_VALUE.test(val)) out.push(`${prop}: ${val}`);
  }
  return out.join('; ');
}

const SAFE_URL = /^(https?:|mailto:|tel:|#)/i;
const SAFE_IMG_URL = /^(https?:|data:image\/(png|jpe?g|gif|webp|bmp);base64,)/i;

function cleanUrl(value: string, forImage: boolean): string | null {
  // Vezérlő- és szóközkarakterek eltávolítása (pl. "java\nscript:" trükk ellen)
  const v = value.replace(/[\u0000- \u007f-\u009f]/g, '');
  if (!v) return null;
  if (forImage ? SAFE_IMG_URL.test(v) : SAFE_URL.test(v)) return value.trim();
  // Relatív hivatkozás (séma nélkül) – biztonságos
  if (!/^[a-z][a-z0-9+.-]*:/i.test(v) && !forImage) return value.trim();
  return null;
}

function sanitizeNode(node: Node, doc: Document): void {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.COMMENT_NODE) {
      child.parentNode?.removeChild(child);
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const el = child as Element;
    const tag = el.tagName.toLowerCase();

    if (DROP_WITH_CONTENT.has(tag)) {
      el.parentNode?.removeChild(el);
      continue;
    }

    if (!ALLOWED_TAGS.has(tag)) {
      // Ismeretlen elem: a gyerekeit megtartjuk, magát az elemet kicsomagoljuk
      sanitizeNode(el, doc);
      const frag = doc.createDocumentFragment();
      while (el.firstChild) frag.appendChild(el.firstChild);
      el.parentNode?.replaceChild(frag, el);
      continue;
    }

    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name === 'style' && STYLE_TAGS.has(tag)) {
        const st = cleanStyle(attr.value);
        if (st) el.setAttribute('style', st); else el.removeAttribute('style');
        continue;
      }
      const allowed = ALLOWED_ATTRS['*'].has(name) || ALLOWED_ATTRS[tag]?.has(name);
      if (!allowed) { el.removeAttribute(attr.name); continue; }
      if (name === 'href' || name === 'src') {
        const safe = cleanUrl(attr.value, name === 'src');
        if (safe === null) el.removeAttribute(attr.name);
      }
    }

    if (tag === 'a') {
      // Külső hivatkozás új lapon, opener-hozzáférés nélkül
      if (el.getAttribute('href') && !String(el.getAttribute('href')).startsWith('#')) {
        el.setAttribute('target', '_blank');
        el.setAttribute('rel', 'noopener noreferrer nofollow');
      }
    }

    sanitizeNode(el, doc);
  }
}

/** Biztonságos HTML-t ad vissza; böngészőn kívül (SSR) minden jelölést escape-el. */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') {
    return String(html).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
  }
  // A DOMParser által létrehozott dokumentum inert: a szkriptek nem futnak, a képek nem töltődnek.
  const doc = new DOMParser().parseFromString(`<!DOCTYPE html><html><body>${html}</body></html>`, 'text/html');
  sanitizeNode(doc.body, doc);
  return doc.body.innerHTML;
}

const BLOCK_TAGS = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'tr', 'blockquote', 'pre', 'table', 'ul', 'ol']);

/** Formázott (HTML) leírásból sima szöveg – a szövegalapú felhasználásokhoz (export, keresés, PHP-tükör) */
export function htmlToPlainText(html: string | null | undefined): string {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const parts: string[] = [];
  const walk = (n: Node, listDepth: number) => {
    n.childNodes.forEach((c) => {
      if (c.nodeType === Node.TEXT_NODE) { parts.push((c.textContent || '').replace(/\s+/g, ' ')); return; }
      if (c.nodeType !== Node.ELEMENT_NODE) return;
      const el = c as Element;
      const tag = el.tagName.toLowerCase();
      if (tag === 'br') { parts.push('\n'); return; }
      if (tag === 'img') { const alt = el.getAttribute('alt'); if (alt) parts.push(`[${alt}]`); return; }
      if (tag === 'li') parts.push('\n' + '  '.repeat(Math.max(0, listDepth - 1)) + '• ');
      if (tag === 'td' || tag === 'th') parts.push(' | ');
      walk(el, tag === 'ul' || tag === 'ol' ? listDepth + 1 : listDepth);
      if (tag === 'a') {
        const href = el.getAttribute('href') || '';
        const txt = (el.textContent || '').trim();
        const shown = href.replace(/^(mailto:|tel:)/i, '');
        if (href && !href.startsWith('#') && !/^tel:/i.test(href) && !txt.includes(shown)) parts.push(` (${shown})`);
      }
      if (BLOCK_TAGS.has(tag) && tag !== 'li') parts.push('\n');
    });
  };
  walk(doc.body, 0);
  return parts.join('').replace(/[ \t]+\n/g, '\n').replace(/\n[ \t]+(?!• )/g, '\n').replace(/\n{3,}/g, '\n\n').replace(/^\s*\|\s*/gm, '').trim();
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const LINKIFY = /(https?:\/\/[^\s<>"]+[^\s<>".,;:!?)]|[\w.+-]+@[\w-]+(\.[\w-]+)+)/g;

/** Sima szövegből formázott kiindulás: bekezdések, sortörések, kattintható linkek / e-mail-címek */
export function plainTextToHtml(text: string | null | undefined): string {
  if (!text) return '';
  return String(text).replace(/\r\n/g, '\n').split(/\n{2,}/).map((para) => {
    const lines = para.split('\n').map((l) => esc(l).replace(LINKIFY, (m) => (m.includes('@') && !m.startsWith('http') ? `<a href="mailto:${m}">${m}</a>` : `<a href="${m}">${m}</a>`)));
    return `<p>${lines.join('<br>')}</p>`;
  }).join('');
}

export default sanitizeHtml;
